import { createHash } from 'node:crypto';
import {
  canonicalUrl,
  pageForPathname,
  structuredData,
  type SeoPage,
} from '../../../shared/src/seo';

export interface SeoRender {
  html: string;
  status: number;
  scriptHashes: string[];
}

const HEAD_PATTERN = /<!--seo-head-->[\s\S]*?<!--\/seo-head-->/;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function jsonForScript(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

export function renderSeoHead(page: SeoPage, origin: string, requestPath: string): { html: string; scriptHash: string } {
  const pageUrl = canonicalUrl(origin, page, requestPath);
  const siteOrigin = new URL(origin).origin;
  const json = jsonForScript(structuredData(page, pageUrl, siteOrigin));
  const scriptHash = createHash('sha256').update(json, 'utf8').digest('base64');
  const title = escapeHtml(page.title);
  const description = escapeHtml(page.description);
  const url = escapeHtml(pageUrl);
  const robots = escapeHtml(page.robots);
  const html = [
    '<!--seo-head-->',
    `<title>${title}</title>`,
    `<meta name="description" content="${description}" />`,
    `<meta name="robots" content="${robots}" />`,
    `<link rel="canonical" href="${url}" />`,
    '<meta property="og:type" content="website" />',
    '<meta property="og:site_name" content="PHP2Shell Scanner" />',
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:url" content="${url}" />`,
    '<meta name="twitter:card" content="summary" />',
    `<meta name="twitter:title" content="${title}" />`,
    `<meta name="twitter:description" content="${description}" />`,
    `<script id="structured-data" type="application/ld+json">${json}</script>`,
    '<!--/seo-head-->',
  ].join('\n');
  return { html, scriptHash };
}

export function applyPageSeo(template: string, pathname: string, origin: string): SeoRender {
  const page = pageForPathname(pathname);
  const head = renderSeoHead(page, origin, pathname);
  const html = HEAD_PATTERN.test(template)
    ? template.replace(HEAD_PATTERN, head.html)
    : template.replace('</head>', `${head.html}\n</head>`);
  return {
    html,
    status: page.id === 'not-found' ? 404 : 200,
    scriptHashes: [head.scriptHash],
  };
}

export function stampScriptNonces(html: string, nonce: string): string {
  const safe = nonce.replace(/[^A-Za-z0-9+/=]/g, '');
  return html.replace(/<script\b([^>]*)>/gi, (full, attrs: string) => {
    if (/\snonce\s*=/i.test(attrs)) return full;
    return `<script nonce="${safe}"${attrs}>`;
  });
}

export function publicOrigin(siteUrl: string | null, hostHeader: string | undefined, protocol: string): string {
  if (siteUrl) return siteUrl;
  const host = hostHeader?.trim() ?? '';
  if (!/^[a-z0-9.-]+(?::\d{1,5})?$/i.test(host)) {
    return 'http://127.0.0.1:3001';
  }
  const scheme = protocol === 'https' ? 'https' : 'http';
  return `${scheme}://${host}`;
}
